import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import JsonLd from "@/components/JsonLd";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { PACKAGES } from "@/lib/data";
import { MANIFESTO_IMAGE } from "@/lib/data";
import { useCms } from "@/context/ContentContext";
import { dedupeBySlug } from "@/lib/cms";

const PKG_FAQS = [
  {
    q: "Are these fixed departures or fully customisable?",
    a: "Both. Use any itinerary as it is, or tell us the client's dates, pace and hotel category and we'll rework it — durations and inclusions are flexible.",
  },
  {
    q: "Do you show retail prices?",
    a: "No. Rates are shared as confidential net rates for agents on request, so you set your own selling price.",
  },
  {
    q: "What does a typical package include?",
    a: "Accommodation, private transport with driver, permits where needed and on-ground coordination. Flights and personal expenses are usually excluded unless you ask us to add them.",
  },
  {
    q: "Can you handle both groups and FIT?",
    a: "Yes — from FIT couples to large groups and coaches, with rooming lists respected and a lead coordinator for bigger movements.",
  },
  {
    q: "How quickly will I get a quote?",
    a: "Send a brief through the enquiry form or WhatsApp and our trade desk responds within 24 hours with net rates.",
  },
];

const REGION_FILTERS = [
  { key: "all", label: "All regions", match: null },
  { key: "sikkim", label: "Sikkim", match: "sikkim" },
  { key: "arunachal", label: "Arunachal", match: "arunachal" },
  { key: "meghalaya", label: "Meghalaya", match: "meghalaya" },
  { key: "assam", label: "Assam", match: "assam" },
  { key: "nagaland", label: "Nagaland", match: "nagaland" },
  { key: "bhutan", label: "Bhutan", match: "bhutan" },
  { key: "nepal", label: "Nepal", match: "nepal" },
  { key: "darjeeling", label: "Darjeeling", match: "darjeeling" },
];

const nightsOf = (p) => {
  const m = String(p.days || "").match(/(\d+)\s*Night/i);
  return m ? parseInt(m[1], 10) : 0;
};

const DURATION_FILTERS = [
  { key: "all", label: "Any length", test: null },
  { key: "short", label: "Short (≤4 nights)", test: (n) => n > 0 && n <= 4 },
  { key: "week", label: "5–7 nights", test: (n) => n >= 5 && n <= 7 },
  { key: "grand", label: "Grand (8+ nights)", test: (n) => n >= 8 },
];

const textOf = (p) => `${p.title || ""} ${(p.tags || []).join(" ")} ${p.region || ""} ${p.summary || ""}`.toLowerCase();

function FilterRow({ label, options, active, onChange, prefix }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <span className="text-xs uppercase tracking-[0.2em] text-[var(--tx-text-2)] w-24 shrink-0">{label}</span>
      <div className="flex flex-wrap gap-2.5">
        {options.map((f) => {
          const isActive = f.key === active;
          return (
            <button
              key={f.key}
              data-testid={`${prefix}-filter-${f.key}`}
              onClick={() => onChange(f.key)}
              className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${isActive ? "border-[var(--tx-gold)] text-[var(--tx-gold)] bg-[var(--tx-gold)]/5" : "border-[var(--tx-border)] text-[var(--tx-text-2)] hover:border-[var(--tx-gold)]/50 hover:text-[var(--tx-text)]"}`}
            >
              {f.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const THEME_FILTERS = [
  { key: "all", label: "All themes", match: null },
  { key: "trek", label: "Trek & Nature", match: (t) => /trek|valley|hike|flower|lake|trail|dzukou/.test(t) },
  { key: "wildlife", label: "Wildlife", match: (t) => /kaziranga|wildlife|safari|rhino|national park|manas/.test(t) },
  { key: "culture", label: "Culture & Festivals", match: (t) => /hornbill|festival|monaster|heritage|tribe|apatani|kisama|culture/.test(t) },
  { key: "multi", label: "Multi-state", match: (t) => /multi-state|seven sisters|grand|circuit|frontier/.test(t) },
];

export default function Packages() {
  usePageMeta({
    title: "North East India & Himalayan Tour Packages for Agents | TripTrix DMC",
    description:
      "Ready-to-sell, fully customisable tour packages across North East India, Bhutan, Nepal & Darjeeling — day-wise itineraries with net rates for the trade.",
    keywords:
      "North East India tour packages for agents, Sikkim tour packages net rates, Meghalaya tour packages, Bhutan tour packages B2B, Darjeeling packages for travel agents, wholesale North East India itineraries, customisable North East India tour packages, honeymoon group MICE packages North East India, ready to sell itineraries for tour operators, net rates on request",
  });
  const cms = useCms();
  const [filter, setFilter] = useState("all");
  const [duration, setDuration] = useState("all");
  const [theme, setTheme] = useState("all");
  const allPackages = dedupeBySlug(PACKAGES, cms.packages);
  const region = REGION_FILTERS.find((f) => f.key === filter) || REGION_FILTERS[0];
  const dur = DURATION_FILTERS.find((f) => f.key === duration) || DURATION_FILTERS[0];
  const thm = THEME_FILTERS.find((f) => f.key === theme) || THEME_FILTERS[0];
  const visible = allPackages.filter((p) => {
    if (region.match && !(p.region || "").toLowerCase().includes(region.match)) return false;
    if (dur.test && !dur.test(nightsOf(p))) return false;
    if (thm.match && !thm.match(textOf(p))) return false;
    return true;
  });
  const reset = () => { setFilter("all"); setDuration("all"); setTheme("all"); };
  return (
    <>
      <PageHeader
        overline="For the travel trade"
        title="Tour packages for travel agents"
        sub="Use these as ready itineraries or a starting point we tailor to your client — durations, hotel category and pace are all flexible. Rates are shared as confidential net rates for agents, never as retail."
        image={MANIFESTO_IMAGE}
      />

      {/* Bold discovery stat band */}
      <section className="border-b border-[var(--tx-border)] bg-[var(--tx-bg-2)]" data-testid="packages-stat-band">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:divide-x divide-[var(--tx-border)]">
          <div className="text-center sm:text-left sm:pr-8">
            <div className="font-display text-5xl md:text-6xl font-light text-[var(--tx-gold)]" data-testid="packages-total-count">{allPackages.length}</div>
            <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Ready itineraries to tailor</div>
          </div>
          <div className="text-center sm:text-left sm:px-8">
            <div className="font-display text-5xl md:text-6xl font-light text-[var(--tx-gold)]">11+</div>
            <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Himalayan destinations</div>
          </div>
          <div className="text-center sm:text-left sm:pl-8">
            <div className="font-display text-5xl md:text-6xl font-light text-[var(--tx-gold)]">100%</div>
            <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Net rates for agents</div>
          </div>
        </div>
      </section>

      <section className="pb-28 pt-14" data-testid="packages-list">
        {/* Filters */}
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 space-y-5" data-testid="packages-filters">
          <FilterRow label="Region" options={REGION_FILTERS} active={filter} onChange={setFilter} prefix="region" />
          <FilterRow label="Duration" options={DURATION_FILTERS} active={duration} onChange={setDuration} prefix="duration" />
          <FilterRow label="Theme" options={THEME_FILTERS} active={theme} onChange={setTheme} prefix="theme" />
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm text-[var(--tx-text-2)]" data-testid="packages-visible-count">
              Showing <span className="text-[var(--tx-gold)]">{visible.length}</span> of {allPackages.length} journeys
            </span>
            {(filter !== "all" || duration !== "all" || theme !== "all") && (
              <button onClick={reset} data-testid="packages-reset" className="text-sm text-[var(--tx-text-2)] hover:text-[var(--tx-gold)] transition-colors duration-300">Clear filters</button>
            )}
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-6 md:px-10 mt-10 grid gap-8 md:grid-cols-2">
          {visible.length === 0 && (
            <div className="md:col-span-2 border border-dashed border-[var(--tx-border)] p-12 text-center text-[var(--tx-text-2)]" data-testid="packages-empty">
              No journeys match these filters. <button onClick={reset} className="text-[var(--tx-gold)] hover:underline">Clear filters</button> or tell us your dream trip.
            </div>
          )}
          {visible.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.08}>
              <div className="group h-full flex flex-col md:flex-row border border-[var(--tx-border)] bg-[var(--tx-bg-2)] overflow-hidden hover:border-[var(--tx-gold)]/40 transition-colors duration-500">
                <Link to={`/packages/${p.slug}`} className="relative md:w-2/5 h-56 md:h-auto overflow-hidden block">
                  <img src={p.image} alt={p.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </Link>
                <div className="flex flex-1 flex-col p-7">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">{p.days}</span>
                    <span className="text-xs uppercase tracking-[0.18em] text-[var(--tx-gold)]">{p.region}</span>
                  </div>
                  <h3 className="mt-3 font-display text-2xl md:text-3xl font-light leading-tight">{p.title}</h3>
                  <p className="mt-3 text-sm text-[var(--tx-text-2)] leading-relaxed flex-1">{p.summary}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {(p.tags || []).map((t) => (
                      <span key={t} className="rounded-full border border-[var(--tx-border)] px-3 py-1 text-xs text-[var(--tx-text)]/70">{t}</span>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-between pt-5 border-t border-[var(--tx-border)]">
                    <span className="text-sm text-[var(--tx-gold)]">Net rates on request</span>
                    <Link to={`/packages/${p.slug}`} className="inline-flex items-center gap-1.5 text-sm hover:text-[var(--tx-gold)] transition-colors duration-300">
                      View itinerary <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mx-auto max-w-[1400px] px-6 md:px-10 mt-20">
          <Reveal>
            <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-10 md:p-14 text-center">
              <h3 className="font-display text-3xl md:text-4xl font-light">Don&apos;t see the right itinerary?</h3>
              <p className="mt-4 text-[var(--tx-text-2)] max-w-xl mx-auto">Send us the brief — client profile, dates and budget band — and we&apos;ll build a costed itinerary at net rates.</p>
              <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--tx-gold)] px-8 py-3.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300">
                Get a custom quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ + FAQPage schema */}
      <section className="border-t border-[var(--tx-border)] py-20 md:py-28" data-testid="packages-faq">
        <div className="mx-auto max-w-[900px] px-6 md:px-10">
          <h2 className="font-display font-light tracking-tight text-3xl md:text-4xl">Questions agents ask about our packages</h2>
          <div className="mt-10 divide-y divide-[var(--tx-border)]">
            {PKG_FAQS.map((f) => (
              <div key={f.q} className="py-6">
                <h3 className="font-display text-xl font-light text-[var(--tx-text)]">{f.q}</h3>
                <p className="mt-3 text-[var(--tx-text-2)] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <JsonLd
        id="jsonld-packages-faq"
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: PKG_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
    </>
  );
}
